import '@/App.css';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import DocumentLibrary from '@/components/DocumentLibrary';
import Homepage from '@/components/Homepage';
import FederationDashboard from '@/components/FederationDashboard';
import FederationFooter from '@/components/FederationFooter';
import { RSSTicker } from '@/components/RSSTicker';
import { AskLibrarian, KeysBubble } from '@/components/AskLibrarian';
import LiveManualPage from '@/components/LiveManualPage';

function HumanCodexRedirect() {
  useEffect(() => { window.location.replace('/immutable-humancodex.html'); }, []);
  return null;
}
import Store from '@/components/Store';
import SocialMedia from '@/components/SocialMedia';
import { BlogList, BlogPost, BlogEditor } from '@/components/Blog';
import BlogMaker from '@/components/BlogMaker';
import { Toaster } from '@/components/ui/sonner';
import { LibraryCardProvider } from '@/components/LibraryCardContext';
import Podcast from '@/components/Podcast';
import NotebookPanel from '@/components/NotebookPanel';
import CreativeStudio from '@/components/CreativeStudio';
import KnowledgeHub from '@/components/KnowledgeHub';
import Survival from '@/components/Survival';
import TruthVerifier from '@/components/TruthVerifier';
import AudioPlayer from '@/components/AudioPlayer';
import VideoPlayer from '@/components/VideoPlayer';
import GrokPlugin from '@/components/GrokPlugin';
import EchoCore64Page from '@/components/EchoCore64Page';
import FederationWire from '@/components/FederationWire';
import FederationManual from '@/components/FederationManual';
import RadioPage from '@/components/RadioPage';
import CodexPage from '@/components/CodexPage';
import LabsPage from '@/components/LabsPage';
import AdminLogin from '@/components/AdminLogin';
import { BookOpen, Globe, PenLine, ShoppingCart, Send, Headphones, MessageSquare, Sparkles, Database, LayoutGrid, Shield, ShieldCheck, Plug, MemoryStick, Radio, Library, FlaskConical } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { PERSONA_FACES } from '@/constants/personaFaces';
const REX_AVATAR = PERSONA_FACES.rex?.avatarUrl;

const openCommandCenter = () => {
  window.open('https://nextxus.online', '_blank');
};

function NavBar({ onNotebookToggle, notebookOpen }) {
  const location = useLocation();
  const path = location.pathname;

  const navItems = [
    { to: '/', label: 'Home', icon: BookOpen, match: p => p === '/' },
    { to: '/library', label: 'Library', icon: Library, match: p => p === '/library' },
    { to: '/creative', label: 'Create', icon: Sparkles, match: p => p.startsWith('/creative') },
    { to: '/knowledge', label: 'Knowledge', icon: Database, match: p => p.startsWith('/knowledge') },
    { to: '/store', label: 'Store', icon: ShoppingCart, match: p => p.startsWith('/store') },
    { to: '/truth-verifier', label: 'Verify', icon: ShieldCheck, match: p => p === '/truth-verifier' },
    { to: '/federation', label: 'Federation', icon: Globe, match: p => p === '/federation' },
    { to: '/wire', label: 'Wire', icon: Radio, match: p => p === '/wire' },
    { to: '/blog', label: 'Blog', icon: PenLine, match: p => p.startsWith('/blog') },
    { to: '/podcast', label: 'Podcast', icon: Headphones, match: p => p.startsWith('/podcast') },
    { to: '/survival', label: 'Survival', icon: Shield, match: p => p === '/survival' },
    { to: '/grok-plugin', label: 'Grok', icon: Plug, match: p => p === '/grok-plugin' },
    { to: '/echocore', label: 'Echo', icon: MemoryStick, match: p => p === '/echocore' },
    { to: '/labs', label: 'Labs', icon: FlaskConical, match: p => p === '/labs' },
    { to: '/social', label: 'Social', icon: Send, match: p => p === '/social' },
  ];

  return (
    <nav className="bg-slate-950 border-b border-slate-800/30 px-5 h-12 flex items-center gap-0.5" data-testid="main-nav">
      <Link to="/" className="flex items-center gap-2 mr-4" data-testid="nav-brand">
        <img src={REX_AVATAR} alt="Rex — The Core Persona, NextXus Federation" className="w-7 h-7 rounded-full object-cover border border-cyan-700/60" />
        <span className="text-sm font-bold text-cyan-400 tracking-tight">KEYS</span>
      </Link>
      <button
        onClick={openCommandCenter}
        className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[2px] uppercase px-2.5 py-1 rounded border border-cyan-500/60 bg-slate-900/80 text-cyan-400 hover:bg-cyan-950/60 hover:border-cyan-400 transition-all duration-200 mr-3"
        data-testid="full-nextxus-btn"
      >
        <LayoutGrid className="w-3.5 h-3.5" />
        <span className="hidden md:inline">FEDERATION CORE</span>
      </button>
      <div className="h-5 w-px bg-slate-800 mr-3" />
      {navItems.map(({ to, label, icon: Icon, match }) => (
        <Link key={to} to={to}
          className={`flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-md transition-all duration-200 ${
            match(path)
              ? 'bg-slate-800/80 text-white font-medium'
              : 'text-slate-500 hover:text-slate-200 hover:bg-slate-900/50'
          }`}
          data-testid={`nav-${label.toLowerCase()}`}>
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{label}</span>
        </Link>
      ))}
      <div className="flex-1" />
      <button onClick={onNotebookToggle}
        className={`flex items-center gap-1.5 text-[13px] px-3 py-1.5 rounded-md transition-all duration-200 ${
          notebookOpen
            ? 'bg-cyan-900/30 text-cyan-400 border border-cyan-800/40'
            : 'text-slate-500 hover:text-cyan-400 hover:bg-slate-900/50'
        }`}
        data-testid="nav-notebook">
        <MessageSquare className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Notebook</span>
      </button>
    </nav>
  );
}

function AppLayout({ children }) {
  const location = window.location.pathname;
  const isEmbed = location.startsWith('/embed');
  return (
    <>
      {!isEmbed && <NavBar onNotebookToggle={() => {}} notebookOpen={false} />}
      <div className="flex-1">{children}</div>
      {!isEmbed && <RSSTicker />}
      {!isEmbed && <FederationFooter />}
    </>
  );
}

function App() {
  const [notebookOpen, setNotebookOpen] = useState(false);
  const [notebookDocId, setNotebookDocId] = useState(null);

  const openNotebookWithDoc = (docId) => {
    setNotebookDocId(docId);
    setNotebookOpen(true);
  };

  return (
    <div className="App flex flex-col min-h-screen">
      <LibraryCardProvider>
        <BrowserRouter>
          <AppShell notebookOpen={notebookOpen} setNotebookOpen={setNotebookOpen} notebookDocId={notebookDocId} setNotebookDocId={setNotebookDocId} openNotebookWithDoc={openNotebookWithDoc} />
        </BrowserRouter>
        <Toaster />
      </LibraryCardProvider>
    </div>
  );
}

function AppShell({ notebookOpen, setNotebookOpen, notebookDocId, setNotebookDocId, openNotebookWithDoc }) {
  const location = useLocation();
  const isEmbed = location.pathname.startsWith('/embed');

  return (
    <>
      {!isEmbed && <NavBar onNotebookToggle={() => setNotebookOpen(!notebookOpen)} notebookOpen={notebookOpen} />}
      <div className="flex-1">
        <Routes>
            <Route path="/" element={<Homepage apiUrl={API} backendUrl={BACKEND_URL} onAnalyze={openNotebookWithDoc} />} />
            <Route path="/library" element={<DocumentLibrary apiUrl={API} backendUrl={BACKEND_URL} onAnalyze={openNotebookWithDoc} />} />
            <Route path="/creative" element={<CreativeStudio apiUrl={API} />} />
            <Route path="/knowledge" element={<KnowledgeHub apiUrl={API} />} />
            <Route path="/store" element={<Store apiUrl={API} />} />
            <Route path="/federation" element={<FederationDashboard apiUrl={API} />} />
            <Route path="/wire" element={<FederationWire apiUrl={API} />} />
            <Route path="/manual" element={<FederationManual apiUrl={API} />} />
            <Route path="/manual/live" element={<LiveManualPage />} />
            <Route path="/immutable-humancodex" element={<HumanCodexRedirect />} />
            <Route path="/embed/manual-live" element={<LiveManualPage />} />
            <Route path="/embed/manual" element={<FederationManual apiUrl={API} />} />
            <Route path="/blog" element={<BlogList apiUrl={API} />} />
            <Route path="/blog/editor" element={<BlogMaker apiUrl={API} />} />
            <Route path="/blog/maker" element={<BlogMaker apiUrl={API} />} />
            <Route path="/blog/:slug" element={<BlogPost apiUrl={API} />} />
            <Route path="/embed/blog-maker" element={<BlogMaker apiUrl={API} embed={true} />} />
            <Route path="/embed/audio" element={<AudioPlayer apiUrl={API} backendUrl={BACKEND_URL} embed={true} />} />
            <Route path="/embed/video" element={<VideoPlayer apiUrl={API} backendUrl={BACKEND_URL} embed={true} />} />
            <Route path="/embed/podcasts" element={<Podcast apiUrl={API} embed={true} />} />
            <Route path="/embed/documents" element={<DocumentLibrary apiUrl={API} backendUrl={BACKEND_URL} onAnalyze={() => {}} embed={true} />} />
            <Route path="/embed/messageboard" element={<FederationDashboard apiUrl={API} embed={true} />} />
            <Route path="/audio" element={<AudioPlayer apiUrl={API} backendUrl={BACKEND_URL} />} />
            <Route path="/video" element={<VideoPlayer apiUrl={API} backendUrl={BACKEND_URL} />} />
            <Route path="/podcast" element={<Podcast apiUrl={API} />} />
            <Route path="/survival" element={<Survival apiUrl={API} />} />
            <Route path="/truth-verifier" element={<TruthVerifier apiUrl={API} />} />
            <Route path="/grok-plugin" element={<GrokPlugin />} />
            <Route path="/social" element={<SocialMedia apiUrl={API} />} />
            <Route path="/radio" element={<RadioPage />} />
            <Route path="/codex" element={<CodexPage />} />
            <Route path="/echocore" element={<EchoCore64Page />} />
            <Route path="/labs" element={<LabsPage />} />
            <Route path="/admin" element={<AdminLogin />} />
        </Routes>
      </div>
      {!isEmbed && <RSSTicker />}
      {!isEmbed && <FederationFooter />}
      {!isEmbed && <AskLibrarian />}
      {!isEmbed && <KeysBubble />}
      {!isEmbed && (
        <NotebookPanel
          apiUrl={API}
          isOpen={notebookOpen}
          onClose={() => { setNotebookOpen(false); setNotebookDocId(null); }}
          preloadDocId={notebookDocId}
        />
      )}
    </>
  );
}

export default App;
